import React, { useState } from 'react';
import { Church, ChevronDown, Coins, LogOut, UserCheck, Shield, Building, Sun, Moon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Role } from '../../types';

export const Header: React.FC = () => {
  const {
    currentUser,
    currentTenant,
    availableTenants,
    availableUsers,
    switchTenant,
    switchUser,
    logout,
  } = useAuth();

  const { theme, toggleTheme } = useTheme();

  const [isTenantMenuOpen, setIsTenantMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';

  const getRoleLabel = (role?: Role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'ADMIN':
        return 'Church Admin';
      case 'USER':
      default:
        return 'Church User';
    }
  };

  const getRoleBadgeStyle = (role?: Role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30';
      case 'ADMIN':
        return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30';
      case 'USER':
      default:
        return 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/30';
    }
  };

  return (
    <header className="border-b border-slate-200 dark:border-slate-800/80 bg-white/95 dark:bg-slate-950/90 backdrop-blur-xl sticky top-0 z-50 transition-colors">
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-12 h-16 flex items-center justify-between">
        {/* Brand & Logo */}
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 via-indigo-500 to-amber-400 flex items-center justify-center shadow-md shadow-indigo-500/20">
            <Church className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm tracking-tight text-slate-900 dark:text-white">Safe AI</span>
              <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border border-indigo-500/20">
                Ministry Platform
              </span>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-normal">
              Scripture-Safe Voice Synthesis
            </p>
          </div>
        </div>

        {/* Center: Tenant Switcher or Super Admin Status */}
        <div className="hidden sm:flex items-center space-x-3">
          {isSuperAdmin ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-purple-500/10 border border-purple-500/25 text-xs text-purple-600 dark:text-purple-300 font-semibold">
              <Shield className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
              <span>Platform Owner (SaaS Master)</span>
            </div>
          ) : (
            <>
              {/* Church Selector */}
              <div className="relative">
                <button
                  onClick={() => {
                    setIsTenantMenuOpen(!isTenantMenuOpen);
                    setIsUserMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 dark:bg-slate-900 dark:border-slate-800 dark:hover:border-slate-700 text-xs font-semibold text-slate-800 dark:text-slate-200 transition-all shadow-sm cursor-pointer"
                >
                  <Building className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                  <span>{currentTenant?.name || 'Select Church'}</span>
                  <ChevronDown className="w-3 h-3 text-slate-400" />
                </button>

                {isTenantMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 rounded-2xl bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-800 shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95">
                    <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                      Church Organization
                    </div>
                    {availableTenants.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => {
                          switchTenant(t.slug);
                          setIsTenantMenuOpen(false);
                        }}
                        className={`w-full text-left px-2.5 py-2 rounded-xl text-xs font-medium flex items-center justify-between transition-colors ${
                          currentTenant?.slug === t.slug
                            ? 'bg-indigo-600/10 text-indigo-600 dark:bg-indigo-600/20 dark:text-indigo-300 border border-indigo-500/30 font-semibold'
                            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
                        }`}
                      >
                        <span>{t.name}</span>
                        <span className="text-[10px] text-amber-600 dark:text-amber-400 font-mono">
                          {(t.creditsBalance / 1000).toFixed(0)}k credits
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Church Credits Balance Display */}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 border border-slate-200 text-amber-700 dark:bg-slate-900 dark:border-slate-800 dark:text-amber-300 text-xs font-semibold">
                <Coins className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                <span>{(currentTenant?.creditsBalance || 0).toLocaleString()} Credits</span>
              </div>
            </>
          )}
        </div>

        {/* Right Section: Theme Toggle & User Profile */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          {/* Light / Dark Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-slate-300 dark:border-slate-800 transition-colors cursor-pointer"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            aria-label="Toggle Theme"
          >
            {theme === 'dark' ? (
              <Sun className="w-4 h-4 text-amber-400" />
            ) : (
              <Moon className="w-4 h-4 text-indigo-600" />
            )}
          </button>

          {/* User Profile & Role Switcher Menu */}
          <div className="relative">
            <button
              onClick={() => {
                setIsUserMenuOpen(!isUserMenuOpen);
                setIsTenantMenuOpen(false);
              }}
              className="flex items-center gap-2 p-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-200 dark:bg-slate-900 dark:border-slate-800 dark:hover:border-slate-700 transition-all cursor-pointer"
            >
              <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-indigo-600 to-amber-500 flex items-center justify-center text-xs font-bold text-white shadow-inner">
                {currentUser?.name.charAt(0) || 'U'}
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-xs font-semibold text-slate-900 dark:text-white leading-tight">
                  {currentUser?.name}
                </div>
                <div className="mt-0.5">
                  <span
                    className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.2 rounded border ${getRoleBadgeStyle(
                      currentUser?.role,
                    )}`}
                  >
                    {getRoleLabel(currentUser?.role)}
                  </span>
                </div>
              </div>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>

            {isUserMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-72 rounded-2xl bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-800 shadow-2xl p-2 z-50 animate-in fade-in zoom-in-95">
                <div className="px-2.5 py-2 border-b border-slate-200 dark:border-slate-800/80 mb-1">
                  <div className="text-xs font-bold text-slate-900 dark:text-white">{currentUser?.name}</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{currentUser?.email}</div>
                  <div className="mt-1">
                    <span
                      className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${getRoleBadgeStyle(
                        currentUser?.role,
                      )}`}
                    >
                      Role: {getRoleLabel(currentUser?.role)}
                    </span>
                  </div>
                </div>

                <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                  Simulate Role Identity (RBAC)
                </div>
                {availableUsers.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => {
                      switchUser(u.id);
                      setIsUserMenuOpen(false);
                    }}
                    className={`w-full text-left px-2.5 py-2 rounded-xl text-xs font-medium flex items-center justify-between transition-colors ${
                      currentUser?.id === u.id
                        ? 'bg-indigo-600/10 text-indigo-600 dark:bg-indigo-600/20 dark:text-indigo-300 font-semibold'
                        : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                      <div>
                        <div>{u.name}</div>
                        <div className="text-[10px] text-slate-500">{getRoleLabel(u.role)}</div>
                      </div>
                    </div>
                    {currentUser?.id === u.id && (
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 dark:bg-indigo-400" />
                    )}
                  </button>
                ))}

                <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-800/80">
                  <button
                    onClick={() => {
                      logout();
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full px-2.5 py-1.5 rounded-xl text-xs font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10 flex items-center gap-2 transition-colors cursor-pointer"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
